import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  ButtonBase,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { keyframes } from '@mui/material/styles';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import VideocamOffOutlinedIcon from '@mui/icons-material/VideocamOffOutlined';
import { formatDuration } from './videoApi';

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.25; }
`;

/* Small toolbar pill for the assessment recording: red dot + time while
   recording, a live preview under it (tap to hide), pause/stop, then
   «saving…» and «saved to the assessment». When recording is off it is a
   camera button that turns it on. */
export default function LiveRecordingPill({
  live,
  en,
  enabled,
  onEnable,
  onStop,
}) {
  const videoRef = useRef(null);
  const [preview, setPreview] = useState(true);
  const [showSaved, setShowSaved] = useState(false);

  useEffect(() => {
    if (
      videoRef.current &&
      live.stream &&
      videoRef.current.srcObject !== live.stream
    )
      videoRef.current.srcObject = live.stream;
  }, [live.stream, preview]);

  useEffect(() => {
    if (live.status !== 'saved') return undefined;
    setShowSaved(true);
    const t = setTimeout(() => setShowSaved(false), 5000);
    return () => clearTimeout(t);
  }, [live.status]);

  const pillSx = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.75,
    height: 32,
    px: 1.25,
    borderRadius: 999,
    color: '#fff',
    bgcolor: 'rgba(255,255,255,0.14)',
    fontWeight: 700,
    fontSize: '0.82rem',
    whiteSpace: 'nowrap',
  };

  if (live.status === 'starting')
    return (
      <Box sx={pillSx}>
        <CircularProgress size={14} color="inherit" />
        {live.native
          ? en
            ? 'Screen + camera…'
            : 'Экран + камера…'
          : en
            ? 'Camera…'
            : 'Камера…'}
      </Box>
    );

  if (live.status === 'finishing')
    return (
      <Box sx={pillSx}>
        <CircularProgress size={14} color="inherit" />
        {en ? 'Saving video…' : 'Сохраняю видео…'}
        {live.native && typeof live.progress === 'number'
          ? ` ${Math.round(live.progress * 100)}%`
          : ''}
      </Box>
    );

  if (live.status === 'recording' || live.status === 'paused') {
    const rec = live.status === 'recording';
    return (
      <Box sx={{ position: 'relative' }}>
        <Box
          sx={{
            ...pillSx,
            pr: 0.25,
            bgcolor: rec ? 'rgba(220,38,38,0.9)' : 'rgba(255,255,255,0.18)',
          }}
        >
          <ButtonBase
            onClick={() => setPreview((v) => !v)}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              borderRadius: 999,
            }}
            aria-label={
              en ? 'Show or hide the camera' : 'Показать или скрыть камеру'
            }
          >
            <Box
              sx={{
                width: 9,
                height: 9,
                borderRadius: '50%',
                bgcolor: '#fff',
                animation: rec ? `${pulse} 1.2s ease-in-out infinite` : 'none',
              }}
            />
            {rec ? 'REC' : en ? 'Paused' : 'Пауза'}{' '}
            {formatDuration(live.elapsed)}
            {live.backlog > 2 && (
              <Tooltip
                title={
                  en
                    ? 'Waiting for the network to upload the video'
                    : 'Видео ждёт сети, чтобы догрузиться'
                }
              >
                <WarningAmberIcon sx={{ fontSize: 16 }} />
              </Tooltip>
            )}
          </ButtonBase>
          <Tooltip
            title={
              rec
                ? en
                  ? 'Pause recording'
                  : 'Пауза записи'
                : en
                  ? 'Resume'
                  : 'Продолжить запись'
            }
          >
            <IconButton
              size="small"
              color="inherit"
              onClick={rec ? live.pause : live.resume}
            >
              {rec ? (
                <PauseIcon fontSize="small" />
              ) : (
                <PlayArrowIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              en
                ? 'Stop and save the video now'
                : 'Остановить и сохранить видео сейчас'
            }
          >
            <IconButton size="small" color="inherit" onClick={onStop}>
              <StopIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        {preview && live.stream && (
          <Box
            component="video"
            ref={videoRef}
            autoPlay
            muted
            playsInline
            onClick={() => setPreview(false)}
            sx={{
              position: 'absolute',
              top: 40,
              right: 0,
              width: { xs: 96, sm: 128 },
              aspectRatio: '4 / 3',
              objectFit: 'cover',
              borderRadius: '10px',
              border: '2px solid rgba(220,38,38,0.9)',
              bgcolor: '#000',
              transform: 'scaleX(-1)',
              boxShadow: 6,
              zIndex: 5,
              cursor: 'pointer',
            }}
          />
        )}
      </Box>
    );
  }

  if (showSaved && live.status === 'saved')
    return (
      <Box sx={{ ...pillSx, bgcolor: 'rgba(46,125,91,0.9)' }}>
        <CheckCircleIcon sx={{ fontSize: 16 }} />
        {en ? 'Video saved to the assessment' : 'Видео сохранено в оценку'}
      </Box>
    );

  if (live.status === 'error')
    return (
      <Tooltip title={live.error || ''}>
        <ButtonBase
          onClick={onEnable}
          sx={{ ...pillSx, bgcolor: 'rgba(232,169,61,0.9)', color: '#2a1a00' }}
        >
          <WarningAmberIcon sx={{ fontSize: 16 }} />
          {en ? 'No video — retry' : 'Без видео — повторить'}
        </ButtonBase>
      </Tooltip>
    );

  if (!enabled)
    return (
      <Tooltip
        title={
          en
            ? 'Record this assessment on video'
            : 'Записывать эту проверку на видео'
        }
      >
        <IconButton
          color="inherit"
          onClick={onEnable}
          aria-label={en ? 'Turn recording on' : 'Включить запись'}
        >
          <VideocamOffOutlinedIcon />
        </IconButton>
      </Tooltip>
    );

  return null;
}
